import React, { useCallback, useEffect, useState } from "react";
import {
  maintenanceApi,
  ProcDevice,
  ProcPoolSummary,
  ProcStage,
} from "../../../api/maintenance";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import { STAGES, stageMeta, errMsg } from "./procUtils";
import DeviceDrawer from "./DeviceDrawer";
import AddToOrderForm from "./AddToOrderForm";
import { Search, ChevronLeft, ChevronRight, FileText } from "lucide-react";

interface Props {
  summary: ProcPoolSummary | null;
  refreshKey: number;
  onChanged: (msg?: string) => void;
  onError: (msg: string) => void;
}

const PAGE_SIZE = 50;

/** The pool: every minted identity and where it currently is. */
const PoolTab: React.FC<Props> = ({ summary, refreshKey, onChanged, onError }) => {
  const [devices, setDevices] = useState<ProcDevice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [stage, setStage] = useState<ProcStage | "">("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showAddToOrder, setShowAddToOrder] = useState(false);

  const canEdit = canEditMaintenance();

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await maintenanceApi.procGetDevices({
        stage: stage || undefined,
        search: search || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setDevices(res.data.items);
      setTotal(res.data.total);
    } catch (e: any) {
      onError(errMsg(e, "Failed to load the device pool"));
    } finally {
      setLoading(false);
    }
  }, [stage, search, page, onError]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices, refreshKey]);

  // Only un-ordered devices can go onto an order; anything else is not selectable.
  const selectable = devices.filter((d) => d.stage === "generated" && !d.order_no);
  const allSelected = selectable.length > 0 && selectable.every((d) => selected.has(d.id));

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = (on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      selectable.forEach((d) => (on ? next.add(d.id) : next.delete(d.id)));
      return next;
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      {/* stage tiles double as filters */}
      <div className="proc-tiles">
        {STAGES.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`proc-tile ${stage === s.key ? "sel" : ""}`}
            title={s.desc}
            onClick={() => {
              setStage(stage === s.key ? "" : s.key);
              setPage(1);
            }}
          >
            <div className="n">{summary ? (summary as any)[s.key] ?? 0 : "—"}</div>
            <div className="l">
              <span className={`proc-dot ${s.dot}`} />
              {s.label}
            </div>
          </button>
        ))}
      </div>

      <div className="maint-toolbar">
        <form
          className="maint-search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput.trim());
            setPage(1);
          }}
        >
          <Search size={16} color="#999" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search device ID, serial, batch, manufacturer or station…"
          />
        </form>
        <div className="proc-mini">
          {stage ? `Filtered to ${stageMeta(stage).label.toLowerCase()} · ` : ""}
          {total} device{total === 1 ? "" : "s"}
        </div>
      </div>

      {loading ? (
        <p className="maint-loading">Loading…</p>
      ) : (
        <table className="maint-table">
          <thead>
            <tr>
              {canEdit && (
                <th style={{ width: 34, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    className="proc-cbx"
                    checked={allSelected}
                    disabled={selectable.length === 0}
                    onChange={(e) => toggleAll(e.target.checked)}
                    title="Select all un-ordered devices on this page"
                  />
                </th>
              )}
              <th>Device ID</th>
              <th>Stage</th>
              <th>Batch</th>
              <th>Hardware</th>
              <th>Serial</th>
              <th>Deployed / order</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => {
              const s = stageMeta(d.stage);
              const canPick = d.stage === "generated" && !d.order_no;
              return (
                <tr
                  key={d.id}
                  className={`clickable ${selected.has(d.id) ? "sel" : ""}`}
                  onClick={() => setOpenId(d.id)}
                >
                  {canEdit && (
                    <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="proc-cbx"
                        checked={selected.has(d.id)}
                        disabled={!canPick}
                        onChange={() => toggle(d.id)}
                        title={
                          canPick
                            ? "Select to add to an order"
                            : d.order_no
                            ? `Already on order ${d.order_no}`
                            : "Only un-ordered devices can be added"
                        }
                      />
                    </td>
                  )}
                  <td className="proc-mono">{d.unique_id}</td>
                  <td>
                    <span className={`proc-pill ${s.cls}`}>{s.label}</span>
                  </td>
                  <td>{d.batch_name || <span style={{ color: "#bbb" }}>—</span>}</td>
                  <td>
                    {d.cp_vendor || d.cp_model ? (
                      <>
                        {d.cp_vendor} {d.cp_model}
                      </>
                    ) : (
                      <span style={{ color: "#bbb" }}>—</span>
                    )}
                  </td>
                  <td>{d.serial_no || <span style={{ color: "#bbb" }}>—</span>}</td>
                  <td>
                    {d.station_name ? (
                      <>
                        {d.station_name}
                        {d.display_name && (
                          <span style={{ color: "#999", fontSize: 12 }}> · {d.display_name}</span>
                        )}
                      </>
                    ) : d.order_no ? (
                      <span className="proc-mini">
                        <FileText size={12} style={{ verticalAlign: -2 }} /> {d.order_no}
                      </span>
                    ) : (
                      <span style={{ color: "#bbb" }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {devices.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 7 : 6} className="maint-empty">
                  {search || stage
                    ? "No devices match."
                    : "The pool is empty — mint a batch or import device_configuration.csv."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div className="maint-pagination">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="maint-pagination-controls">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft size={16} />
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="proc-bulkbar">
          <span>
            <b>{selected.size}</b> device{selected.size === 1 ? "" : "s"} selected
          </span>
          <button className="maint-btn-primary" onClick={() => setShowAddToOrder(true)}>
            Add to order
          </button>
          <button className="link" onClick={() => setSelected(new Set())}>
            Clear
          </button>
        </div>
      )}

      {showAddToOrder && (
        <AddToOrderForm
          deviceIds={Array.from(selected)}
          onClose={() => setShowAddToOrder(false)}
          onDone={(orderNo) => {
            setShowAddToOrder(false);
            setSelected(new Set());
            onChanged(`Added ${selected.size} device(s) to ${orderNo}`);
          }}
        />
      )}

      {openId !== null && (
        <DeviceDrawer
          deviceId={openId}
          onClose={() => setOpenId(null)}
          onChanged={(msg) => {
            setOpenId(null);
            onChanged(msg);
          }}
        />
      )}
    </>
  );
};

export default PoolTab;
